import type { GetTriageListOutputBody, UserProfile } from "@partners/ui/api";

import { useFeatureFlags } from "@partners/ui";
import {
  GetTriageListOutputBodyStatus,
  TriagePartnerType,
  useGetTriages,
  useGetUser,
  UserProfileOnboardingState,
} from "@partners/ui/api";
import { useMemo } from "react";

type IBOnboardingState = {
  latestIBTriage: GetTriageListOutputBody | undefined;
  isIBFromNewFlow?: boolean | undefined;
  isOldFlowReapply?: boolean | undefined;
  redirectTo?: string;
  isLoading: boolean;
  isFetching: boolean;
  user?: UserProfile;
};

type UseIBOnboardingStateCheckOptions = {
  skipMAMAffiliateRedirectCheck?: boolean;
  allowOldFlowReapply?: boolean;
};

export const useIBOnboardingStateCheck = (
  options?: UseIBOnboardingStateCheckOptions,
): IBOnboardingState => {
  const { skipMAMAffiliateRedirectCheck = false, allowOldFlowReapply = false } =
    options ?? {};
  const { ibRejectedReapply } = useFeatureFlags();

  const {
    data: user,
    isLoading: isUserLoading,
    isFetching: isUserFetching,
  } = useGetUser(undefined, {
    query: {
      refetchOnMount: true,
      refetchOnWindowFocus: false,
    },
  });

  const {
    data: triages,
    isLoading: isTriagesLoading,
    isFetching: isTriagesFetching,
  } = useGetTriages();

  const latestIBTriage = useMemo(
    () => triages?.find((t) => t.details.partnerType === TriagePartnerType.IB),
    [triages],
  );

  const latestMAMTriage = useMemo(
    () => triages?.find((t) => t.details.partnerType === TriagePartnerType.MAM),
    [triages],
  );

  const latestAffiliateTriage = useMemo(
    () =>
      triages?.find(
        (t) => t.details.partnerType === TriagePartnerType.affiliate,
      ),
    [triages],
  );

  const isFetching = isUserFetching || isTriagesFetching;

  const result = useMemo<IBOnboardingState>(() => {
    const isLoading = isUserLoading || isTriagesLoading;
    const { onboardingState } = user || {};
    let baseResult: IBOnboardingState = {
      latestIBTriage,
      isLoading,
      isFetching,
      user,
    };

    if (isLoading) {
      return baseResult;
    }

    const isIBNotComplete =
      onboardingState === UserProfileOnboardingState.IBApplicationNotComplete;

    const isIBCompleted =
      onboardingState === UserProfileOnboardingState.IBApplicationComplete;

    baseResult.isIBFromNewFlow = user?.isQuickStart ?? false;
    baseResult.isOldFlowReapply =
      ibRejectedReapply && !!user?.isRejected && !user?.isQuickStart;

    const keepInTriage = allowOldFlowReapply && baseResult.isOldFlowReapply;

    if ((isIBNotComplete || isIBCompleted) && !keepInTriage) {
      return {
        ...baseResult,
        redirectTo: "/",
      };
    }

    if (!skipMAMAffiliateRedirectCheck) {
      const hasMAMMaster = user?.isMAMMaster === true;
      const hasMAMTriageComplete =
        latestMAMTriage?.status === GetTriageListOutputBodyStatus.COMPLETE;
      const hasAffiliateComplete =
        latestAffiliateTriage?.status ===
        GetTriageListOutputBodyStatus.COMPLETE;

      if (hasMAMMaster || hasMAMTriageComplete || hasAffiliateComplete) {
        return {
          ...baseResult,
          redirectTo: "/triage/history",
        };
      }
    }

    return {
      ...baseResult,
      redirectTo: undefined,
    };
  }, [
    isUserLoading,
    isTriagesLoading,
    user,
    latestIBTriage,
    latestMAMTriage,
    latestAffiliateTriage,
    isFetching,
    skipMAMAffiliateRedirectCheck,
    allowOldFlowReapply,
    ibRejectedReapply,
  ]);

  return result;
};
